import React, { useCallback, useState, useEffect } from "react";
import "./FriendlyUrlComponent.css";
import axiosConfig from "../../Service/axiosConfig";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import Blog from "../Footer/Blogs";
import BlogDetail from "../Footer/BlogDetail";
import {
  ProductDetailPage,
} from "../../Pages";
import CategoriesPage from "../../Pages/CategoriesPage";
import NotFound from "../../Pages/NotFound";
import SubCategoriesPage from "../../Pages/SubCategoriesPage";
import { buildSeoPath, getCategoryIdFromProductData } from "../../Utils/routes";

const getRedirectedSlug = async (slug) => {
  const visitedSlugs = new Set([slug]);
  let currentSlug = slug;

  while (currentSlug) {
    const response = await axiosConfig.get(
      `/catlog/redirectrouter/?old_slug=${currentSlug}`
    );

    const nextSlug = response.data.results?.[0]?.seourl?.keyword;

    if (!nextSlug || visitedSlugs.has(nextSlug)) {
      return nextSlug && nextSlug !== slug ? nextSlug : null;
    }

    visitedSlugs.add(nextSlug);
    currentSlug = nextSlug;
  }

  return null;
};

const FriendlyUrlComponent = () => {
  let { subcategoryurl, categoryurl, friendlyurl } = useParams();
  const location = useLocation();
  let navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [is404, setis404] = useState(false);
  const [ProductObject, setProductObject] = useState({
    product_type: "",
    product_data: null,
  });
  const GetData = useCallback(async () => {
    const prefetchedProductType = location.state?._prefetched_product_type;
    const prefetchedProductData = location.state?._prefetched_product_data;

    if (prefetchedProductType && prefetchedProductData) {
      setProductObject({
        product_type: prefetchedProductType,
        product_data: prefetchedProductData,
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    setis404(false);

    try {
      let resolvedSlug = friendlyurl;
      let response;

      try {
        response = await axiosConfig.get(
          `/catlog/seo-url/${friendlyurl}/?store=${localStorage.getItem("storeId") || ""}`
        );
      } catch (seoError) {
        const redirectedSlug = await getRedirectedSlug(friendlyurl);

        if (!redirectedSlug) {
          throw seoError;
        }

        resolvedSlug = redirectedSlug;
        response = await axiosConfig.get(
          `/catlog/seo-url/${redirectedSlug}/?store=${localStorage.getItem("storeId") || ""}`
        );
      }

      const { product_type, product_data } = response.data;
      let canonicalPath = buildSeoPath(location.pathname);

      if (product_type === "subcategory") {
        const categoryId = getCategoryIdFromProductData(product_data);
        let resolvedCategorySlug = categoryurl;

        if (!resolvedCategorySlug && categoryId) {
          const categoryResponse = await axiosConfig.get(
            `/catlog/categories/${categoryId}/`
          );
          resolvedCategorySlug = categoryResponse.data?.slug;
        }

        if (resolvedCategorySlug) {
          canonicalPath = buildSeoPath(resolvedCategorySlug, resolvedSlug);
        }
      } else if (resolvedSlug !== friendlyurl && !categoryurl && !subcategoryurl) {
        canonicalPath = buildSeoPath(resolvedSlug);
      }

      if (canonicalPath !== buildSeoPath(location.pathname)) {
        navigate(canonicalPath, {
          replace: true,
          state: {
            _prefetched_product_type: product_type,
            _prefetched_product_data: product_data,
          },
        });
        return;
      }

      setProductObject({
        product_type,
        product_data,
      });
    } catch (error) {
      setis404(true);
    } finally {
      setLoading(false);
    }
  }, [categoryurl, friendlyurl, location.pathname, location.state, navigate, subcategoryurl]);

  useEffect(() => {
    GetData();
  }, [GetData]);

  return (
    <>
      {is404 ? (
        <>
          <NotFound />
        </>
      ) : (
        <div className="categories-container">
          {loading ? (
            <div className="loader-container">
              <img src={require("../images/loader.gif")} alt="Loading..." />
            </div>
          ) : (
            <div className="">
              {ProductObject.product_type === "category" && (
                <>
                  <CategoriesPage
                    category_id={ProductObject.product_data.id}
                    category_slug={friendlyurl}
                  />
                  <Helmet>
                    <title>{ProductObject.product_data.meta_title}</title>
                    <meta property="og:title" content={ProductObject.product_data.meta_title} />
                    <meta name="description" content={ProductObject.product_data.meta_description} />
                    <meta name="og:description" content={ProductObject.product_data.meta_description} />
                    <meta property="keywords" content={ProductObject.product_data.meta_keyword} />
                    <meta property="og:keywords" content={ProductObject.product_data.meta_keyword} />
                    {ProductObject.product_data.image &&
                      <meta name="og:image" property="og:image" content={ProductObject.product_data.image.image} />
                    }
                  </Helmet>
                </>

              )}
              {ProductObject.product_type === "blogcategory" &&
                <Blog category={ProductObject.product_data.id} />
              }
              {ProductObject.product_type === "blog" &&
                <BlogDetail item={ProductObject.product_data} />
              }
              {(ProductObject.product_type === "subcategory" ||
                ProductObject.product_type === "promotionalcategory") && (
                  <>
                    <SubCategoriesPage
                      subcategory_id={
                        ProductObject.product_type === "subcategory"
                          ? ProductObject.product_data.id
                          : null
                      }
                      category_id={getCategoryIdFromProductData(ProductObject.product_data)}
                      promo_id={
                        ProductObject.product_type === "promotionalcategory"
                          ? ProductObject.product_data.id
                          : null
                      }
                      // ADD THESE NEW PROPS for promotional categories
                      // promotional_data={
                      //   ProductObject.product_type === "promotionalcategory"
                      //     ? ProductObject.product_data
                      //     : null
                      // }
                      promotional_data={ProductObject.product_data}
                      directProducts={
                        ProductObject.product_type === "promotionalcategory"
                          ? ProductObject.product_data.products
                          : null
                      }
                      category_slug={categoryurl || friendlyurl}
                      friendlyurl={friendlyurl}
                    />
                    <Helmet>
                      <title>{ProductObject.product_data.meta_title}</title>
                      <meta property="og:title" content={ProductObject.product_data.meta_title} />
                      <meta name="description" content={ProductObject.product_data.meta_description} />
                      <meta name="og:description" content={ProductObject.product_data.meta_description} />
                      <meta property="keywords" content={ProductObject.product_data.meta_keyword} />
                      <meta property="og:keywords" content={ProductObject.product_data.meta_keyword} />
                      {ProductObject.product_data.image &&
                        <meta name="og:image" property="og:image" content={ProductObject.product_data.image.image} />
                      }
                    </Helmet>
                  </>

                )}
              {ProductObject.product_type === "product" && (
                <>
                  <ProductDetailPage
                    product_data={ProductObject.product_data}
                    friendlyurl={friendlyurl}
                    categoryurl={categoryurl}
                    subcategoryurl={subcategoryurl}
                  />
                  <Helmet>
                    <title>{ProductObject.product_data.meta_title}</title>
                    <meta property="og:title" content={ProductObject.product_data.meta_title} />
                    <meta name="description" content={ProductObject.product_data.meta_description} />
                    <meta name="og:description" content={ProductObject.product_data.meta_description} />
                    <meta property="keywords" content={ProductObject.product_data.meta_keyword} />
                    <meta property="og:keywords" content={ProductObject.product_data.meta_keyword} />
                    {ProductObject.product_data.image &&
                      <meta name="og:image" property="og:image" content={ProductObject.product_data.image.image} />
                    }
                  </Helmet>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};
export { FriendlyUrlComponent };
